// validation.js
const Joi = require('@hapi/joi');

const sendMessageSchema = Joi.object({
  type: Joi.string().valid('SEND_MESSAGE'),
  payload: Joi.object({
    message: Joi.string().replace(/{{}}/gi, ''),
  }).required(),
});

const transferToSkillSchema = Joi.object({
  type: Joi.string().valid('TRANSFER_TO_SKILL'),
  payload: Joi.object({
    skillId: [Joi.string().replace(/{{}}/gi, ''), Joi.number()],
    skillName: Joi.string().replace(/{{}}/gi, ''),
  }).min(1).required(),
});

const transferToAgentSchema = Joi.object({
  type: Joi.string().valid('TRANSFER_TO_AGENT'),
  payload: Joi.object({
    skillId: [Joi.string().replace(/{{}}/gi, ''), Joi.number()],
    skillName: Joi.string().replace(/{{}}/gi, ''),
    userId: [Joi.string().replace(/{{}}/gi, ''), Joi.number()],
    agentId: [Joi.string().replace(/{{}}/gi, ''), Joi.number()],
    timeoutInSeconds: Joi.number(),
  }).min(1).required(),
});

const actionsSchema = Joi.alternatives(
  sendMessageSchema,
  transferToSkillSchema,
  transferToAgentSchema,
);

const conditionSchemaV1 = Joi.object({
  property: Joi.string().required(),
  type: Joi.string().required(),
  value: [
    Joi.string().replace(/{{}}/gi, '').replace(/%%%%/gi, '').replace(/\[]/gi, '')
      .required(),
    Joi.boolean().required(),
    Joi.number().required(),
    Joi.array()
      .items(
        Joi.string().replace(/{{}}/gi, ''),
      )
      .required(),
  ],
}).required();

const conditionSchemaV2 = Joi.object({
  property: Joi.string().regex(/(^[a-zA-Z0-9_.]+$)|(^'([^"']+)'$)/).required(),
  type: Joi.string().required(),
  value: [
    Joi.string().replace(/{{}}/gi, '').replace(/%%%%/gi, '').replace(/\[]/gi, '')
      .required(),
    Joi.boolean().required(),
    Joi.number().required(),
    Joi.array()
      .items(
        Joi.string().replace(/{{}}/gi, ''),
      )
      .required(),
  ],
}).required();

const validation = {
  removeErrorKeysFromJson(obj) {
    // need to clean actual temporary policy, not copy
    const cleanedPolicy = obj;

    if (typeof cleanedPolicy === 'object' && cleanedPolicy !== null) {
      Object.keys(cleanedPolicy).forEach((key) => {
        if (key === 'errorArray') {
          delete cleanedPolicy[key];
        }

        if (typeof cleanedPolicy[key] === 'object') {
          this.removeErrorKeysFromJson(cleanedPolicy[key]);
        }
      });
    }
    return cleanedPolicy;
  },

  validatePolicy: (policy, isCcsV2, isComplexEditor) => {
    const policySchemaV1 = Joi.object({
      enabled: Joi.boolean().required(),
      name: Joi.string().required(),
      conditions: Joi.array().items(conditionSchemaV1).required(),
      actions: Joi.array().items(actionsSchema).required(),
      exception: Joi.object({
        actions: Joi.array().items(actionsSchema),
        conditions: Joi.array().items(conditionSchemaV1),
      }),
    });

    const policySchemaV2 = Joi.object({
      enabled: Joi.boolean().required(),
      name: Joi.string().required(),
      conditions: Joi.array().items(conditionSchemaV2).required(),
      actions: Joi.array().items({
        type: Joi.string().valid('SEND_MESSAGE', 'TRANSFER_TO_SKILL', 'TRANSFER_TO_AGENT'),
        payload: Joi.when('type', [
          {
            is: 'SEND_MESSAGE',
            then: Joi.object({
              message: Joi.string().replace(/{{}}/gi, '').required(),
            }).required(),
          },
          {
            is: 'TRANSFER_TO_SKILL',
            then: Joi.object({
              skillId: [Joi.string().replace(/{{}}/gi, ''), Joi.number()],
              skillName: Joi.string().replace(/{{}}/gi, ''),
            }).min(1).required(),
          },
          {
            is: 'TRANSFER_TO_AGENT',
            then: Joi.object({
              skillId: [Joi.string().replace(/{{}}/gi, ''), Joi.number()],
              userId: [Joi.string().replace(/{{}}/gi, ''), Joi.number()],
            }).min(1).required(),
          },
        ]),
      }),
    });

    const policySchemaV2WithComplexEditor = Joi.object({
      enabled: Joi.boolean().required(),
      name: Joi.string().required(),
      condition: Joi.string().required(),
      actions: Joi.array().items({
        type: Joi.string().valid('SEND_MESSAGE', 'TRANSFER_TO_SKILL', 'TRANSFER_TO_AGENT'),
        payload: Joi.when('type', [
          {
            is: 'SEND_MESSAGE',
            then: Joi.object({
              message: Joi.string().replace(/{{}}/gi, '').required(),
            }).required(),
          },
          {
            is: 'TRANSFER_TO_SKILL',
            then: Joi.object({
              skillId: [Joi.string().replace(/{{}}/gi, ''), Joi.number()],
              skillName: Joi.string().replace(/{{}}/gi, ''),
            }).min(1).required(),
          },
          {
            is: 'TRANSFER_TO_AGENT',
            then: Joi.object({
              skillId: [Joi.string().replace(/{{}}/gi, ''), Joi.number()],
              userId: [Joi.string().replace(/{{}}/gi, ''), Joi.number()],
            }).min(1).required(),
          },
        ]),
      }),
    });

    let response;
    if (isCcsV2 && isComplexEditor) {
      response = policySchemaV2WithComplexEditor.validate(policy, { abortEarly: false });
    } else if (isCcsV2) {
      response = policySchemaV2.validate(policy, { abortEarly: false });
    } else {
      response = policySchemaV1.validate(policy, { abortEarly: false });
    }
    if (response.error) {
      return { passedValidation: false, validationErrors: response.error.details };
    }
    return { passedValidation: true, validationErrors: [] };
  },

  addPolicyErrorProperty: (policy, validationErrors) => {
    const validatedPolicy = { ...policy };
    const propertiesWithErrors = validationErrors.map(
      (item) => item.context.label,
    );

    propertiesWithErrors.forEach((item) => {
      // add exception level error
      if (item.includes('exception')) {
        const [path1, path2, path3, , path5] = item.split(/[.[\]]/);
        const currentProperty = validatedPolicy[path1][path2][parseInt(path3, 10)];
        if (currentProperty) {
          if (!Array.isArray(currentProperty.errorArray)) {
            currentProperty.errorArray = [path5];
          } else {
            currentProperty.errorArray.push(path5);
          }
        }
      }

      const [section, index, , property, fieldName] = item.split(/[.[\]]/);

      // add section level error (no action or condition)
      if (section && !index && !property) {
        const currentProperty = (section === 'name' || section === 'order' || section === 'condition') ? validatedPolicy : validatedPolicy[section];
        if (!Array.isArray(currentProperty.errorArray)) {
          currentProperty.errorArray = [section];
        } else {
          currentProperty.errorArray.push(section);
        }
      }

      // property level error (test for prop and add error)
      if (section && index && property && !fieldName) {
        const currentProperty = validatedPolicy[section][parseInt(index, 10)];
        if (currentProperty) {
          if (!Array.isArray(currentProperty.errorArray)) {
            currentProperty.errorArray = [property];
          } else {
            currentProperty.errorArray.push(property);
          }
        }
      }

      // fieldName level error (test for prop and add error)
      if (section && index && property && fieldName) {
        const currentProperty = validatedPolicy[section][parseInt(index, 10)];
        if (currentProperty) {
          if (!Array.isArray(currentProperty.errorArray)) {
            currentProperty.errorArray = [fieldName];
          } else {
            currentProperty.errorArray.push(fieldName);
          }
        }
      }
    });

    return validatedPolicy;
  },

  validateAttribute: (attribute, isCcsV2) => {
    const attributeSchema = Joi.object().keys({
      type: Joi.string().required(),
      name: Joi.string().regex(/^[a-zA-Z0-9_ ]+$/).required(),
      payload: Joi.any()
        .required()
        .when('type', [
          {
            is: 'static',
            then: Joi.alternatives(
              Joi.string()
                .replace(/{{}}/gi, '')
                .replace(/\[]/gi, ''),
              Joi.boolean(),
              Joi.array(),
              Joi.number(),
              Joi.object(),
            ),
          },
          {
            is: 'faas',
            then: Joi.object()
              .keys({
                faasId: Joi.string().required(),
                body: Joi.object().keys({
                  headers: Joi.array().required(),
                  payload: Joi.alternatives(
                    Joi.string().replace(/{{}}/gi, ''),
                    Joi.boolean(),
                    Joi.number(),
                    Joi.object(),
                    Joi.array(),
                  ),
                }),
              }).unknown(true).required(),
          },
        ]),
    });
    const ccsSchema = Joi.object().keys({
      accountId: Joi.string(),
      documentType: Joi.string(),
      type: Joi.string(),
      name: Joi.string().regex(/^[a-zA-Z0-9_ ]+$/),
      varName: Joi.string().regex(/^[a-zA-Z0-9_ ]+$/),
      tenantId: Joi.string(),
      payload: Joi.any()
        .required()
        .when('type', [
          {
            is: 'static',
            then: Joi.alternatives(
              Joi.string()
                .replace(/{{}}/gi, '')
                .replace(/\[]/gi, ''),
              Joi.boolean(),
              Joi.array(),
              Joi.number(),
              Joi.object(),
            ),
          },
          {
            is: 'faas',
            then: Joi.object()
              .keys({
                faasId: Joi.string().required(),
                body: Joi.object().keys({
                  headers: Joi.array().required(),
                  parameters: Joi.alternatives(
                    Joi.string().replace(/{{}}/gi, ''),
                    Joi.boolean(),
                    Joi.number(),
                    Joi.object(),
                    Joi.array(),
                  ),
                }),
              }).unknown(true).required(),
          },
        ]),
    });

    const response = (isCcsV2)
      ? ccsSchema.validate(attribute, { abortEarly: false })
      : attributeSchema.validate(attribute, { abortEarly: false });

    if (response.error) {
      return { passedValidation: false, validationErrors: response.error.details };
    }
    return { passedValidation: true, validationErrors: [] };
  },

  addAttributeErrorProperty: (attribute, validationErrors) => {
    const validatedContext = { ...attribute };

    const propertiesWithErrors = validationErrors.map((item) => item.context.label);

    propertiesWithErrors.forEach((item) => {
      // single value payload error
      if (!Array.isArray(validatedContext.errorArray)) {
        validatedContext.errorArray = [item];
      } else {
        validatedContext.errorArray.push(item);
      }
    });

    validationErrors.forEach((item) => {
      // multiple value payload error
      if (
        item.context.label === 'payload.body.payload'
        && item.context.details
        && item.context.label
      ) {
        item.context.details.forEach((detail) => {
          if (!validatedContext.errorArray.includes(detail.context.label)) {
            if (!Array.isArray(validatedContext.errorArray)) {
              validatedContext.errorArray = [detail.context.label];
            } else {
              validatedContext.errorArray.push(detail.context.label);
            }
          }
        });
      }
    });

    return validatedContext;
  },
};

export default validation;
